import React, { useState, useEffect, useCallback } from 'react';
import { Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { couponService, couponServiceQa } from '../../../utils/pins_services';
import './PinesCupones.css';

function PinesCupones({ usr_id, permission, token, isQA }) {
  const navigate = useNavigate();
  const sessionToken = token || localStorage.getItem('token');
  const svc = isQA ? couponServiceQa : couponService;
  const envLabel = isQA ? ' QA' : '';

  const [coupons, setCoupons] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  // Modal: crear cupón
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newStripeCoupon, setNewStripeCoupon] = useState('');
  const [newProductIds, setNewProductIds] = useState([]);
  const [creating, setCreating] = useState(false);

  // Modal: confirmar eliminar
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Modal: gestionar productos
  const [manageCoupon, setManageCoupon] = useState(null);
  const [linkProductId, setLinkProductId] = useState('');
  const [linkingProduct, setLinkingProduct] = useState(false);

  const showToast = useCallback((message, variant = 'success') => {
    setToastVariant(variant);
    setToastMessage(message);
  }, []);

  useEffect(() => {
    if (!toastMessage) return undefined;
    const t = setTimeout(() => setToastMessage(''), 5000);
    return () => clearTimeout(t);
  }, [toastMessage]);

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const res = await svc.list(sessionToken);
      if (res === 'INVALID') { navigate('/'); return; }
      setCoupons(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error('Error al cargar cupones:', err);
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  }, [svc, sessionToken, navigate]);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await svc.products(sessionToken);
      if (res === 'INVALID') return;
      const list = Array.isArray(res) ? res : (res?.data ?? []);
      setProducts(list);
    } catch (err) {
      console.error('Error al cargar productos:', err);
    }
  }, [svc, sessionToken]);

  useEffect(() => {
    if (!sessionToken) { navigate('/'); return; }
    fetchCoupons();
    fetchProducts();
  }, [fetchCoupons, fetchProducts, navigate, sessionToken]);

  // ── Crear cupón ────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (creating) return;
    if (!newStripeCoupon.trim() || newProductIds.length === 0) return;
    setCreating(true);
    try {
      const res = await svc.create(newStripeCoupon.trim(), newProductIds, sessionToken);
      if (res === 'INVALID') { navigate('/'); return; }
      if (res?.error) {
        showToast(res.message || 'Error al crear el cupón', 'danger');
        return;
      }
      showToast(`Cupón "${newStripeCoupon.trim()}" creado exitosamente`);
      setShowCreateModal(false);
      setNewStripeCoupon('');
      setNewProductIds([]);
      fetchCoupons();
    } catch (err) {
      showToast('Error al crear el cupón', 'danger');
    } finally {
      setCreating(false);
    }
  };

  const toggleProductSelection = (id) => {
    setNewProductIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  // ── Eliminar cupón ─────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      const res = await svc.remove(deleteTarget.id, sessionToken);
      if (res === 'INVALID') { navigate('/'); return; }
      if (res?.error) {
        showToast(res.message || 'Error al eliminar el cupón', 'danger');
        return;
      }
      showToast(`Cupón "${deleteTarget.stripe_coupon}" eliminado`);
      setDeleteTarget(null);
      fetchCoupons();
    } catch (err) {
      showToast('Error al eliminar el cupón', 'danger');
    } finally {
      setDeleting(false);
    }
  };

  // ── Vincular producto ──────────────────────────────────────────────────────
  const handleLinkProduct = async () => {
    if (!manageCoupon || !linkProductId || linkingProduct) return;
    setLinkingProduct(true);
    try {
      const res = await svc.linkProduct(manageCoupon.id, linkProductId, sessionToken);
      if (res === 'INVALID') { navigate('/'); return; }
      if (res?.error) {
        showToast(res.message || 'Error al vincular el producto', 'danger');
        return;
      }
      showToast('Producto vinculado exitosamente');
      setLinkProductId('');
      // Actualizar el cupón en la lista local
      const updated = await svc.list(sessionToken);
      if (Array.isArray(updated)) {
        setCoupons(updated);
        const refreshed = updated.find((c) => c.id === manageCoupon.id);
        if (refreshed) setManageCoupon(refreshed);
      }
    } catch (err) {
      showToast('Error al vincular el producto', 'danger');
    } finally {
      setLinkingProduct(false);
    }
  };

  // ── Desvincular producto ───────────────────────────────────────────────────
  const handleUnlinkProduct = async (productId, productName) => {
    try {
      const res = await svc.unlinkProduct(manageCoupon.id, productId, sessionToken);
      if (res === 'INVALID') { navigate('/'); return; }
      if (res?.error) {
        showToast(res.message || 'Error al desvincular el producto', 'danger');
        return;
      }
      showToast(`Producto "${productName}" desvinculado`);
      const updated = await svc.list(sessionToken);
      if (Array.isArray(updated)) {
        setCoupons(updated);
        const refreshed = updated.find((c) => c.id === manageCoupon.id);
        if (refreshed) setManageCoupon(refreshed);
        else setManageCoupon(null);
      }
    } catch (err) {
      showToast('Error al desvincular el producto', 'danger');
    }
  };

  // Productos disponibles para vincular (los que aún no están en el cupón)
  const availableProducts = products.filter(
    (p) => !manageCoupon?.products?.some((cp) => cp.id === p.id)
  );

  return (
    <div className="cupones-container">
      <h1 className="cupones-title">CUPONES{envLabel}</h1>
      <div className="cupones-count">Total: {coupons.length}</div>

      <div className="cupones-table-container">
        <Table hover className="cupones-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Código Stripe</th>
              <th>Productos vinculados</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" className="text-center py-4">Cargando...</td>
              </tr>
            ) : coupons.length === 0 ? (
              <tr>
                <td colSpan="4" className="text-center py-4">No hay cupones disponibles</td>
              </tr>
            ) : (
              coupons.map((coupon, index) => (
                <tr key={coupon.id}>
                  <td>{index + 1}</td>
                  <td>
                    <span className="cupones-code">{coupon.stripe_coupon}</span>
                  </td>
                  <td>
                    <div className="cupones-products">
                      {coupon.products && coupon.products.length > 0 ? (
                        coupon.products.map((p) => (
                          <span key={p.id} className="cupones-badge">{p.name}</span>
                        ))
                      ) : (
                        <span className="cupones-no-products">Sin productos</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="cupones-actions">
                      <button
                        type="button"
                        className="btn-cupones-manage"
                        onClick={() => { setManageCoupon(coupon); setLinkProductId(''); }}
                      >
                        Gestionar productos
                      </button>
                      <button
                        type="button"
                        className="btn-cupones-delete"
                        onClick={() => setDeleteTarget(coupon)}
                      >
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* FAB crear */}
      <button
        type="button"
        className="cupones-fab"
        onClick={() => { setShowCreateModal(true); setNewStripeCoupon(''); setNewProductIds([]); }}
        aria-label="Crear cupón"
      >
        +
      </button>

      {/* ── Modal: Crear cupón ─────────────────────────────────────────────── */}
      {showCreateModal && (
        <div className="cupones-backdrop" onClick={() => setShowCreateModal(false)}>
          <div className="cupones-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="cupones-modal-close" onClick={() => setShowCreateModal(false)} aria-label="Cerrar">×</button>
            <h2 className="cupones-modal-title">Nuevo Cupón</h2>

            <div className="cupones-modal-field">
              <label>Código Stripe <span className="required">*</span></label>
              <input
                type="text"
                value={newStripeCoupon}
                onChange={(e) => setNewStripeCoupon(e.target.value.toUpperCase())}
                placeholder="Ej: MAESTRO"
                autoFocus
              />
              <p className="cupones-hint">El cupón debe existir previamente en el dashboard de Stripe.</p>
            </div>

            <div className="cupones-modal-field">
              <label>Productos <span className="required">*</span> (mínimo 1)</label>
              <div className="cupones-product-checklist">
                {products.length === 0 ? (
                  <p className="cupones-hint">Cargando productos...</p>
                ) : (
                  products.map((p) => (
                    <label key={p.id} className="cupones-check-item">
                      <input
                        type="checkbox"
                        checked={newProductIds.includes(p.id)}
                        onChange={() => toggleProductSelection(p.id)}
                      />
                      <span>{p.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <button
              type="button"
              className="cupones-modal-action"
              onClick={handleCreate}
              disabled={creating || !newStripeCoupon.trim() || newProductIds.length === 0}
            >
              {creating ? 'Creando...' : 'Crear Cupón'}
            </button>
          </div>
        </div>
      )}

      {/* ── Modal: Confirmar eliminación ───────────────────────────────────── */}
      {deleteTarget && (
        <div className="cupones-backdrop" onClick={() => { if (!deleting) setDeleteTarget(null); }}>
          <div className="cupones-modal cupones-modal--confirm" onClick={(e) => e.stopPropagation()}>
            <div className="cupones-confirm-icon">!</div>
            <h2 className="cupones-modal-title">Eliminar cupón</h2>
            <p className="cupones-confirm-text">
              ¿Estás seguro de que querés eliminar el cupón{' '}
              <strong>{deleteTarget.stripe_coupon}</strong>?
            </p>
            <p className="cupones-confirm-warning">
              Esta acción eliminará el cupón y todas sus asociaciones a productos en la base de datos.
              El cupón en Stripe <strong>no</strong> será afectado.
            </p>
            <div className="cupones-confirm-actions">
              <button
                type="button"
                className="btn-cupones-cancel"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-cupones-confirm-delete"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Gestionar productos ─────────────────────────────────────── */}
      {manageCoupon && (
        <div className="cupones-backdrop" onClick={() => setManageCoupon(null)}>
          <div className="cupones-modal cupones-modal--manage" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="cupones-modal-close" onClick={() => setManageCoupon(null)} aria-label="Cerrar">×</button>
            <h2 className="cupones-modal-title">
              Productos — <span className="cupones-modal-code">{manageCoupon.stripe_coupon}</span>
            </h2>

            {/* Productos actuales */}
            <div className="cupones-manage-section">
              <h3 className="cupones-section-label">Vinculados actualmente</h3>
              {manageCoupon.products && manageCoupon.products.length > 0 ? (
                <ul className="cupones-product-list">
                  {manageCoupon.products.map((p) => (
                    <li key={p.id} className="cupones-product-item">
                      <span>{p.name}</span>
                      <button
                        type="button"
                        className="btn-cupones-unlink"
                        onClick={() => handleUnlinkProduct(p.id, p.name)}
                        title="Desvincular"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="cupones-hint">Este cupón no tiene productos vinculados.</p>
              )}
            </div>

            {/* Vincular nuevo producto */}
            <div className="cupones-manage-section">
              <h3 className="cupones-section-label">Vincular producto</h3>
              {availableProducts.length === 0 ? (
                <p className="cupones-hint">Todos los productos ya están vinculados.</p>
              ) : (
                <div className="cupones-link-row">
                  <select
                    className="cupones-select"
                    value={linkProductId}
                    onChange={(e) => setLinkProductId(e.target.value)}
                  >
                    <option value="">Seleccionar producto...</option>
                    {availableProducts.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn-cupones-link"
                    onClick={handleLinkProduct}
                    disabled={!linkProductId || linkingProduct}
                  >
                    {linkingProduct ? 'Vinculando...' : 'Vincular'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ─────────────────────────────────────────────────────────── */}
      {toastMessage && (
        <div className={`cupones-toast cupones-toast--${toastVariant}`}>
          <span>{toastMessage}</span>
          <button
            type="button"
            className="cupones-toast-close"
            onClick={() => setToastMessage('')}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

export default PinesCupones;
